import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { ISpendAndStashConfig } from '../types';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface ISearchSpendAndStashRequest {
  pageNumber?: number;
  pageSize?: number;
  searchParams?: {
    debitAccountNumber?: string;
    savingsAccountNumber?: string;
  };
}

interface ISearchSpendAndStashResponse {
  status?: boolean;
  message?: string;
  totalCount?: number;
  data?: ISpendAndStashConfig[];
  errors?: IApiResponseError[];
}

const initialState: IApiState<ISearchSpendAndStashResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const searchSpendAndStashAsync = createAsyncThunk(
  'savings/spendAndStash/search',
  async (
    { pageNumber = 1, pageSize = 10, searchParams = {} }: ISearchSpendAndStashRequest,
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.post<ISearchSpendAndStashResponse>(
        '/savings/spend-and-stash-configuration/search',
        { pageNumber, pageSize, searchParams },
        getSavingsAuthConfig(state),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;
      return rejectWithValue(error.response?.data);
    }
  },
);

export const searchSpendAndStashSlice = createSlice({
  name: 'savings/spendAndStash/search',
  initialState,
  reducers: {
    resetSearchSpendAndStash(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchSpendAndStashAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(searchSpendAndStashAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as ISearchSpendAndStashResponse;
      })
      .addCase(searchSpendAndStashAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetSearchSpendAndStash } = searchSpendAndStashSlice.actions;

export default searchSpendAndStashSlice.reducer;
